export interface Point {
  x: number;
  y: number;
}

export interface Box {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

export type Axis = 'x' | 'y';

export type AlignmentKind = 'center' | 'edge';

export interface AlignmentCandidate {
  value: number;
  kind: AlignmentKind;
  cross: number;
}

export interface AlignmentIndex {
  x: AlignmentCandidate[];
  y: AlignmentCandidate[];
}

export interface AlignmentMatch {
  offset: number;
  line: number;
  cross: number;
}

export function centreOf(box: Box): Point {
  return { x: (box.x1 + box.x2) / 2, y: (box.y1 + box.y2) / 2 };
}

export function boxAround(centre: Point, width: number, height: number): Box {
  return {
    x1: centre.x - width / 2,
    y1: centre.y - height / 2,
    x2: centre.x + width / 2,
    y2: centre.y + height / 2,
  };
}

export function shiftBox(box: Box, offset: Point): Box {
  return {
    x1: box.x1 + offset.x,
    y1: box.y1 + offset.y,
    x2: box.x2 + offset.x,
    y2: box.y2 + offset.y,
  };
}

export function buildAlignmentIndex(boxes: Box[]): AlignmentIndex {
  const index: AlignmentIndex = { x: [], y: [] };

  for (const box of boxes) {
    const centre = centreOf(box);

    index.x.push({ value: box.x1, kind: 'edge', cross: centre.y });
    index.x.push({ value: centre.x, kind: 'center', cross: centre.y });
    index.x.push({ value: box.x2, kind: 'edge', cross: centre.y });

    index.y.push({ value: box.y1, kind: 'edge', cross: centre.x });
    index.y.push({ value: centre.y, kind: 'center', cross: centre.x });
    index.y.push({ value: box.y2, kind: 'edge', cross: centre.x });
  }

  return index;
}

function probesFor(box: Box, axis: Axis): Array<{ value: number; kind: AlignmentKind }> {
  const centre = centreOf(box);

  if (axis === 'x') {
    return [
      { value: centre.x, kind: 'center' },
      { value: box.x1, kind: 'edge' },
      { value: box.x2, kind: 'edge' },
    ];
  }

  return [
    { value: centre.y, kind: 'center' },
    { value: box.y1, kind: 'edge' },
    { value: box.y2, kind: 'edge' },
  ];
}

export function resolveAlignment(
  box: Box,
  index: AlignmentIndex,
  axis: Axis,
  tolerance: number,
  range: number
): AlignmentMatch | null {
  const centre = centreOf(box);
  const cross = axis === 'x' ? centre.y : centre.x;
  const candidates = axis === 'x' ? index.x : index.y;

  for (const probe of probesFor(box, axis)) {
    let best: AlignmentMatch | null = null;
    let bestDistance = Number.POSITIVE_INFINITY;

    for (const candidate of candidates) {
      if (candidate.kind !== probe.kind) {
        continue;
      }
      if (Math.abs(candidate.cross - cross) > range) {
        continue;
      }

      const distance = Math.abs(candidate.value - probe.value);
      if (distance > tolerance || distance >= bestDistance) {
        continue;
      }

      bestDistance = distance;
      best = {
        offset: candidate.value - probe.value,
        line: candidate.value,
        cross: candidate.cross,
      };
    }

    if (best) {
      return best;
    }
  }

  return null;
}

export function snapValueToGrid(value: number, spacing: number): number {
  if (!(spacing > 0)) {
    return value;
  }
  return Math.round(value / spacing) * spacing;
}

export function gridSnapOffset(box: Box, spacing: number): Point {
  const centre = centreOf(box);
  return {
    x: snapValueToGrid(centre.x, spacing) - centre.x,
    y: snapValueToGrid(centre.y, spacing) - centre.y,
  };
}

/** Shifting by the change in offset keeps the unsnapped position, and the grab offset, intact. */
export function offsetDelta(desired: Point, applied: Point): Point {
  return { x: desired.x - applied.x, y: desired.y - applied.y };
}
